// Shared cross-subdomain anonymous id. The website (heru.app) mints the cookie
// `heru_anon_id` on domain `.heru.app` and forwards it across subdomains via the
// `?heru_anon_id=` URL param. The webapp adopts it so backend touchpoint stitch
// can join website + webapp sessions to the same person.

const COOKIE_NAME = 'heru_anon_id';
const URL_PARAM = 'heru_anon_id';
const COOKIE_DOMAIN = '.heru.app';
const COOKIE_MAX_AGE = 63072000; // ~2 years
const LEGACY_LOCAL_STORAGE_KEY = 'heru_anonymous_id';

const readCookie = (name: string): string | null => {
	const match = document.cookie.match(
		new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
	);
	return match ? decodeURIComponent(match[1]) : null;
};

const writeSharedCookie = (value: string): void => {
	try {
		const isHttps = window.location.protocol === 'https:';
		const attrs = [
			`${COOKIE_NAME}=${encodeURIComponent(value)}`,
			`Domain=${COOKIE_DOMAIN}`,
			'Path=/',
			`Max-Age=${COOKIE_MAX_AGE}`,
			'SameSite=Lax',
		];
		if (isHttps) attrs.push('Secure');
		document.cookie = attrs.join('; ');
	} catch {
		// Cookie unavailable — silently fail
	}
};

const readAnonIdFromUrl = (): string | null => {
	try {
		const val = new URLSearchParams(window.location.search).get(URL_PARAM);
		return val && val.trim() ? val.trim() : null;
	} catch {
		return null;
	}
};

const generateId = (): string =>
	typeof crypto !== 'undefined' && 'randomUUID' in crypto
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const getAnonymousId = (): string => {
	if (typeof window === 'undefined') return 'server';
	try {
		const id =
			readCookie(COOKIE_NAME) ||
			readAnonIdFromUrl() ||
			window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY) ||
			generateId();

		// Always (re)write the shared cookie so the contract attributes are
		// applied and the value survives across subdomains.
		writeSharedCookie(id);
		try {
			window.localStorage.setItem(LEGACY_LOCAL_STORAGE_KEY, id);
		} catch {
			// localStorage mirror is best-effort
		}
		return id;
	} catch {
		return 'anonymous';
	}
};
